/**
 * `/internal/models*` contract types — ops-facing model catalog CRUD
 * over platform-api. See ADR-003 (schema) and ADR-004 (contract).
 *
 * Callers: ops-api (ops-console model registration, pricing updates, protocol
 * assignment). Hot-path readers (gateway-worker) still go through
 * `/internal/pricing/models`, which joins models + current pricing + protocols.
 *
 * Wire conventions:
 *  - All payloads snake_case to match existing `/internal/*` contracts and the
 *    underlying D1 column names. (Task spec used camelCase but the rest of the
 *    codebase is rigorously snake_case on the wire; routing-layer drift is the
 *    higher-cost mistake.)
 *  - `effective_from` is ISO-8601 UTC (e.g. `2026-04-18T00:00:00Z`).
 *  - Numeric pricing fields are non-negative finite USD per million tokens
 *    (matches `input_per_mtok_usd` style already in `internal-billing.ts`).
 */
import type { MeteringProfileKind } from './metering.ts';

/**
 * Known protocols that can serve a (vendor, model). Phase 1 ships the two
 * native protocols; cross-protocol rows (e.g. openai model served over the
 * anthropic protocol) are deferred to Phase 2 when the pool's protocol
 * factory lands (ADR-003 §model_protocols).
 */
export type ModelProtocol = 'anthropic' | 'openai';

/** Runtime guard for untrusted input; callees validate before INSERT. */
export const KNOWN_MODEL_PROTOCOLS: readonly ModelProtocol[] = ['anthropic', 'openai'] as const;

export function isModelProtocol(value: unknown): value is ModelProtocol {
  return typeof value === 'string' && (KNOWN_MODEL_PROTOCOLS as readonly string[]).includes(value);
}

export type ModelShelfStatus = 'listed' | 'delisted';

export const KNOWN_MODEL_SHELF_STATUSES: readonly ModelShelfStatus[] = [
  'listed',
  'delisted',
] as const;

export function isModelShelfStatus(value: unknown): value is ModelShelfStatus {
  return (
    typeof value === 'string' && (KNOWN_MODEL_SHELF_STATUSES as readonly string[]).includes(value)
  );
}

// ─── models table ─────────────────────────────────────────────────────────

/**
 * Shape returned for every model row. Mirrors `models` plus a convenience
 * `deleted_at` (null for live entries). Callers filter deleted rows with
 * `?include_deleted=true` opt-in.
 */
export interface ModelRecord {
  vendor: string;
  model: string;
  /** Marketing name, e.g. "Claude Opus 4.7". NULL in DB surfaces as `null`. */
  display_name: string | null;
  /** Free-form family tag, e.g. "claude-4". NULL if not set. */
  family: string | null;
  context_window: number | null;
  /** Hard cap on a single response. NULL when upstream enforces. */
  max_output_tokens: number | null;
  supports_streaming: boolean;
  supports_tool_use: boolean;
  supports_vision: boolean;
  supports_prompt_cache: boolean;
  shelf_status: ModelShelfStatus;
  shelf_updated_at: string | null;
  shelf_reason: string | null;
  created_at: string;
  /** ISO-8601; non-null means soft-deleted and hidden from default list. */
  deleted_at: string | null;
  /**
   * ISO-8601 stamp of when ops marked the model deprecated. Distinct from
   * `deleted_at`: deprecated models are still listable, chargeable, and
   * routable — ops just signals that new adoption should be discouraged.
   * Toggled via POST `/internal/models/:vendor/:model/deprecate` +
   * POST `/internal/models/:vendor/:model/undeprecate`. PATCH does NOT
   * accept this column.
   */
  deprecated_at: string | null;
  /**
   * Short operator-facing explanation shown alongside the deprecation
   * chip. NULL when the model is live. Conventionally <= 512 chars but
   * not enforced in DB so ops can paste fuller migration instructions.
   */
  deprecation_notice: string | null;
  /**
   * Current effective pricing snapshot when the record is returned from the
   * list endpoint. Detail endpoints may omit it and callers should treat
   * missing the same as `null`.
   */
  current_pricing?: ModelPricingRecord | null;
}

export interface CreateModelRequest {
  vendor: string;
  model: string;
  display_name?: string | null;
  family?: string | null;
  context_window?: number | null;
  max_output_tokens?: number | null;
  supports_streaming?: boolean;
  supports_tool_use?: boolean;
  supports_vision?: boolean;
  supports_prompt_cache?: boolean;
  shelf_status?: ModelShelfStatus;
  shelf_reason?: string | null;
}

export interface CreateModelResponse {
  model: ModelRecord;
}

export interface ListModelsResponse {
  models: ModelRecord[];
}

export interface GetModelResponse {
  model: ModelRecord;
}

/**
 * Partial update. Undefined fields are left unchanged. Setting `display_name`
 * or `family` to `null` clears the column. Primary key columns (`vendor`,
 * `model`) are path parameters and cannot be renamed via PATCH.
 */
export interface UpdateModelRequest {
  display_name?: string | null;
  family?: string | null;
  context_window?: number | null;
  max_output_tokens?: number | null;
  supports_streaming?: boolean;
  supports_tool_use?: boolean;
  supports_vision?: boolean;
  supports_prompt_cache?: boolean;
}

export interface UpdateModelResponse {
  model: ModelRecord;
}

export interface DeleteModelResponse {
  model: ModelRecord;
}

/**
 * POST /internal/models/:vendor/:model/deprecate — stamp `deprecated_at`
 * (CURRENT_TIMESTAMP) + `deprecation_notice`. 409 MODEL_ALREADY_DEPRECATED
 * when already stamped (callers update notice via a second
 * undeprecate → deprecate cycle to keep audit rows clean).
 */
export interface DeprecateModelRequest {
  /** Short operator-facing explanation. Must be non-empty. */
  deprecation_notice: string;
}

export interface DeprecateModelResponse {
  model: ModelRecord;
}

/**
 * POST /internal/models/:vendor/:model/undeprecate — clear both
 * `deprecated_at` and `deprecation_notice`. 409 MODEL_NOT_DEPRECATED
 * when the row is already live.
 */
export interface UndeprecateModelResponse {
  model: ModelRecord;
}

export interface UpdateModelShelfRequest {
  reason?: string | null;
}

export interface UpdateModelShelfResponse {
  model: ModelRecord;
}

// ─── model_pricing table (append-only by effective_from) ─────────────────

/**
 * One pricing generation. Historical cost reconstruction picks the row with
 * the greatest `effective_from <= usage_log.created_at`. Earlier rows are
 * retained; callers never mutate them.
 */
export interface ModelPricingRecord {
  vendor: string;
  model: string;
  /** ISO-8601 UTC. When this pricing takes effect. */
  effective_from: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  metering_profile?: MeteringProfileKind | null;
  created_at: string;
}

export interface CreatePricingRequest {
  effective_from: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd?: number;
  cache_write_per_mtok_usd?: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
}

export interface CreatePricingResponse {
  pricing: ModelPricingRecord;
}

export interface ListPricingResponse {
  /** Newest-first by `effective_from`. */
  pricing: ModelPricingRecord[];
}

export interface CurrentPricingResponse {
  pricing: ModelPricingRecord;
}

// ─── model_protocols table ────────────────────────────────────────────────

export interface ModelProtocolRecord {
  vendor: string;
  model: string;
  protocol: ModelProtocol;
}

export interface CreateProtocolRequest {
  protocol: ModelProtocol;
  /** Optional override; omitted means the platform default for the protocol. */
  metering_profile?: MeteringProfileKind;
}

export interface CreateProtocolResponse {
  protocol: ModelProtocolRecord;
}

export interface ListProtocolsResponse {
  protocols: ModelProtocolRecord[];
}

export interface DeleteProtocolResponse {
  protocol: ModelProtocolRecord;
}
